// El descuento por forma de pago vive de dos maneras distintas.
//
// **En el terminal es global**: uno solo sobre el documento, guardado en la
// línea de pago. **En Business Central va repartido por línea**: cada línea
// lleva su porcentaje y su importe, y el documento no tiene ningún descuento
// "de cabecera". Son la misma decisión comercial escrita de dos formas.
//
// Mientras esa regla no cambie, Traer Históricos tiene que reconocer los
// globales al leerlos de vuelta: si no, un documento que salió del terminal con
// un 4% de efectivo vuelve como un documento con 4% en cada línea, que el
// terminal interpreta como descuento especial -- y el descuento especial anula
// al de forma de pago (decisión D5). El resultado es que el ticket y el detalle
// de esa factura dejan de coincidir.
//
// Medido en el ambiente de pruebas de Ecuador el 06/09/2026 con la factura
// PVMDSC1R-007652: sus dos líneas llegan con 4% (126,65 -> 121,58 y
// 73,42 -> 70,48) y su pago en EFECTIVO, que es una de las dos formas con 4%
// configurado en el país junto con TRANS_BANC. Devolviendo ese 4% a global, el
// total del documento da 202,6368 -- exactamente el `Importe` que Business
// Central tiene guardado del cobro. Dejándolo por línea da 202,63, y esa
// diferencia de un céntimo es la que aparecía como "Pendiente" en pantalla.

import { resolveMoneyDecimals, roundMoney } from "./money";

/** Mismo criterio que el resto de los importes: lo que no es número, es cero. */
const toSafeNumber = (valor) => {
  const numero = Number.parseFloat(valor);
  return Number.isFinite(numero) ? numero : 0;
};

/** Las líneas que son producto: los comentarios no llevan descuento. */
const lineasDeProducto = (lines) =>
  (lines || []).filter((linea) => linea && linea.Type !== " " && linea.No);

/**
 * Cuánto puede desviarse el porcentaje de una línea del porcentaje real.
 *
 * Business Central publica el importe del descuento redondeado a céntimos, y el
 * porcentaje se deriva de él: en una línea de 126,65 al 4% el importe es 5,07 y
 * el porcentaje derivado sale 4,0032, no 4. La desviación máxima es medio
 * céntimo repartido sobre el importe de la línea, así que el margen se calcula
 * y no se fija a ojo -- con uno fijo, o se cuela un 4,4% en las líneas grandes
 * o se escapa un 4% legítimo en las pequeñas.
 */
const margenDeLaLinea = (importeDeLinea) => 0.5 / Math.max(Math.abs(importeDeLinea) || 0, 0.01);

/**
 * Deshace el descuento sobre un importe: lo devuelve a precio de tarifa.
 *
 * Hace falta porque el detalle de una factura ya cerrada **no rehace la cuenta**:
 * usa los importes que trajo Business Central (`Line_Amount` y
 * `Amount_Including_VAT`), que son los únicos que cuadran contra el pago al
 * céntimo. Si se quitara el porcentaje de la línea sin devolver también esos
 * importes, el documento quedaría con el descuento aplicado dos veces: una
 * dentro del importe guardado y otra al aplicar el global.
 */
const aPrecioDeTarifa = (valor, porcentaje, decimals) => {
  if (valor == null) return valor;

  const numero = toSafeNumber(valor);
  if (!numero) return valor;

  return roundMoney(numero / (1 - porcentaje / 100), decimals);
};

/**
 * Si el descuento que traen las líneas es en realidad el global del terminal.
 *
 * Se piden dos cosas a la vez, y las dos importan:
 *
 *  1. **Todas** las líneas llevan el porcentaje que el país tiene configurado
 *     para esa forma de pago. Basta que una no lo lleve para que sea un
 *     descuento de línea de verdad.
 *  2. El pago **registra un descuento**. Es lo que distingue este caso de una
 *     campaña que casualmente diera el mismo porcentaje a todas las líneas: esa
 *     no deja ninguna marca en el cobro.
 *
 * @param {Array}  lines       Líneas ya mapeadas (Line_Discount_ en porcentaje).
 * @param {number} porcentaje  El configurado para la forma de pago del cobro.
 * @param {boolean} elPagoTraeDescuento
 */
export const esElDescuentoGlobal = (lines, porcentaje, elPagoTraeDescuento) => {
  const configurado = toSafeNumber(porcentaje);
  if (configurado <= 0 || !elPagoTraeDescuento) return false;

  const suyas = lineasDeProducto(lines);
  if (suyas.length === 0) return false;

  return suyas.every((linea) => {
    const suyo = toSafeNumber(linea.Line_Discount_);
    return Math.abs(suyo - configurado) <= margenDeLaLinea(linea.Line_Amount) + 1e-9;
  });
};

/**
 * Devuelve a global el descuento que Business Central reparte por línea.
 *
 * No inventa importes: quita el porcentaje de las líneas --que vuelven a su
 * precio de tarifa-- y se lo pone al cobro, que es donde el terminal lo espera.
 * A partir de ahí el documento se calcula igual que si se acabara de vender.
 *
 * @param {Object}   params
 * @param {Array}    params.lines     Líneas mapeadas de todos los documentos.
 * @param {Array}    params.payments  Líneas de pago mapeadas de todos ellos.
 * @param {Function} params.dtoDe     (código de forma de pago) => % configurado.
 * @returns {{lines: Array, payments: Array, documentos: Array<string>}}
 */
export const devolverElDescuentoAGlobal = ({
  lines = [],
  payments = [],
  dtoDe,
  country = null,
}) => {
  const decimals = resolveMoneyDecimals(country);

  // Un pago se localiza por su número de borrador o por el definitivo: los
  // documentos contabilizados se cruzan por uno u otro según de dónde vengan.
  const pagosPorDocumento = new Map();
  (payments || []).forEach((pago) => {
    [pago?.Document_No_, pago?.Documento_Definitivo].filter(Boolean).forEach((clave) => {
      pagosPorDocumento.set(String(clave), [...(pagosPorDocumento.get(String(clave)) || []), pago]);
    });
  });

  const lineasPorDocumento = new Map();
  (lines || []).forEach((linea) => {
    const clave = linea?.invoiceNo;
    if (!clave) return;
    lineasPorDocumento.set(String(clave), [
      ...(lineasPorDocumento.get(String(clave)) || []),
      linea,
    ]);
  });

  const globales = new Map();
  lineasPorDocumento.forEach((suyas, clave) => {
    const pagos = pagosPorDocumento.get(clave) || [];
    // Con más de una forma de pago no hay descuento que atribuir: es la misma
    // decisión D5 que aplica el terminal al cobrar.
    if (pagos.length !== 1) return;

    const porcentaje = toSafeNumber(dtoDe?.(pagos[0]?.Payment_Method));
    const traeDescuento = toSafeNumber(pagos[0]?.Dto_Amount) > 0;

    if (esElDescuentoGlobal(suyas, porcentaje, traeDescuento)) globales.set(clave, porcentaje);
  });

  if (globales.size === 0) return { lines, payments, documentos: [] };

  const esGlobal = (clave) => globales.has(String(clave));

  return {
    lines: (lines || []).map((linea) => {
      const porcentaje = globales.get(String(linea?.invoiceNo));
      if (porcentaje == null) return linea;

      // Los tres importes que el mapeo guarda de BC vuelven a tarifa junto con
      // el porcentaje. Dejarlos descontados es lo que hacía que el detalle
      // enseñara 194,52 donde el ticket decía 202,64: el 4% aplicado dos veces.
      return {
        ...linea,
        Line_Discount_: 0,
        Line_Amount: aPrecioDeTarifa(linea.Line_Amount, porcentaje, decimals),
        Total_VAT_Amount: aPrecioDeTarifa(linea.Total_VAT_Amount, porcentaje, decimals),
        Amount_Including_VAT: aPrecioDeTarifa(linea.Amount_Including_VAT, porcentaje, decimals),
      };
    }),
    payments: (payments || []).map((pago) => {
      // El porcentaje pasa a ser el del catálogo, exacto. El que traía venía
      // deducido del importe que publica BC, y ese importe lleva dos
      // convenciones distintas según la fecha del documento (ver
      // descuentoDePagoDelDocumento en invoiceTotals.js).
      const clave = [pago?.Document_No_, pago?.Documento_Definitivo]
        .filter(Boolean)
        .map(String)
        .find(esGlobal);

      return clave ? { ...pago, Dto: globales.get(clave) } : pago;
    }),
    documentos: [...globales.keys()],
  };
};
